import React, { Fragment } from 'react';
import Navbar from '../@layout/NavBar';
import ViewControl from '../@common/ViewControl';
import FilterControl, { FilterOptions } from '../@common/FilterControl';
import AppBar from '../@layout/AppBar';
import AddCreateButton from 'components/@common/Buttons/AddCreateButton';
import { ClassroomModalVariant, ClassroomModalRouteAction } from './types';
import BaseClassroomModal from './Classrooms/modals/BaseClassroomModal';
import { ModuleProps } from 'types';
import useStore from '../../store';
import { useTranslation } from 'react-i18next';

import ClassroomOptionsMenu from './Classrooms/components/ClassroomsOptionsMenu';
import useRouteAction from 'hooks/route-action/use-route-action';
import { ClassroomFilterValue } from './types';
import { TFunction } from 'i18next';
import WithPrivilegedUser from 'components/@layout/WithPrivilegedUser';
import Config from 'config';
import { AlertVariant } from 'types/enums';

const { privilegesGroups } = Config;

const filterLabels: Record<ClassroomFilterValue, string> = {
  [ClassroomFilterValue.ACTIVE]: 'Active',
  [ClassroomFilterValue.ARCHIVE]: 'Archived'
};

type HeaderBarProps = ModuleProps & {
  setLoading: (value: boolean) => void;
  onFilterChange: (value: ClassroomFilterValue) => void;
  selectedFilter: ClassroomFilterValue;
};

const HeaderBar: React.FC<HeaderBarProps> = ({
  setLoading,
  onFilterChange,

  selectedFilter,
  ...props
}) => {
  const [, { openFullModal, closeModal, openAlert }] = useStore();
  const { t } = useTranslation(props.id);

  const getFilterOptions = React.useCallback(
    (t: TFunction) => [
      {
        getValue: (): string => ClassroomFilterValue.ACTIVE,
        getLabel: (): string => t('Active Classes')
      },
      {
        getValue: (): string => ClassroomFilterValue.ARCHIVE,
        getLabel: (): string => t('Archived Classes')
      }
    ],
    []
  );
  const handleClose = React.useCallback(
    (success: boolean) => {
      closeModal();
      if (success) {
        openAlert({
          variant: AlertVariant.SUCCESS,
          title: t('Class Created'),
          text: t('You successfully created your class.'),
          onClose: () => window.location.reload() //TODO: it would be better to make this work without a page reload
        });
      }
    },
    [openAlert, t, closeModal]
  );
  const handleOpenModal = (): void => {
    openFullModal({
      title: t('Create Class'),
      body: (
        <BaseClassroomModal
          handleClose={handleClose}
          classroomModalView={ClassroomModalVariant.CREATE}
        />
      ),
      routeAction: ClassroomModalRouteAction.CREATE_CLASSROOM
    });
  };

  const handleFilterChange = (value: string): void => onFilterChange(value as ClassroomFilterValue);

  useRouteAction(({ action }) => {
    if (!action) return;
    switch (action) {
      case ClassroomModalRouteAction.CREATE_CLASSROOM:
        return handleOpenModal();
      default:
        return null;
    }
  });

  const memoFilterOptions: FilterOptions[] = getFilterOptions(t);

  return (
    <Fragment>
      <AppBar {...props} getTitle={(): string => t('My Classes & Students')}>
        <WithPrivilegedUser privileges={privilegesGroups.createClassroom}>
          <AddCreateButton onClick={handleOpenModal}>{t('Create Class')}</AddCreateButton>
          <ClassroomOptionsMenu setLoading={setLoading} {...props} />
        </WithPrivilegedUser>
      </AppBar>
      <Navbar {...props}>
        <FilterControl
          label={t(filterLabels[selectedFilter])}
          options={memoFilterOptions}
          onChange={handleFilterChange}
          selectedFilter={selectedFilter}
        />
        <ViewControl />
      </Navbar>
    </Fragment>
  );
};

export default HeaderBar;
